import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
// form
import { useFormContext } from 'react-hook-form';
// @mui
import { FormHelperText } from '@mui/material';
//
import { isEmpty } from 'lodash';
import { useDebounce } from 'use-lodash-debounce';
import { updateError } from '../../redux/slices/user';
import { uploadImage } from '../../redux/slices/practiceMessage';
import { getMediaImages } from '../../redux/slices/campaign';
import { dispatch, useSelector } from '../../redux/store';
import { formatMergeTagToken } from '../../utils/mergeTagToken';
import Editor from '../editor/Editor';
import useEmailAttachments from '../../hooks/useEmailAttachments';

// ----------------------------------------------------------------------

export default function RHFEditor({ body, setBody, templateDetails, campaign, EditorMessage, name, screenType, onOpenCustomLib, onCloseCustomLib, setInputFiles, setBase, onSubmit, ...other }) {
    const { type } = { ...other };
    const isLoaded = useRef(true);
    const { superData } = useSelector((state) => state.superAdmin);
    const {
        watch,
        setValue,
        formState: { isSubmitSuccessful, errors },
    } = useFormContext();

    const values = watch();

    const { mergeTags } = useSelector((state) => state.lead);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const [customMergeTags, setCustomMergeTags] = useState({});
    const autoSaveFunction = useDebounce(values?.unlayer_html, 5000);
    const [isAutoSaveOn, setIsAutoSaveOn] = useState(false);
    const {
        dirty: attachmentDirty,
        error: attachmentError,
        hasBlockingError: attachmentBlocked,
        newFiles: attachmentFiles,
        pending: attachmentPending,
        removeSavedAttachment,
        replaceNewFiles,
        savedAttachments,
        templatePayload: attachmentPayload,
        validationError: attachmentValidationError,
    } = useEmailAttachments({
        resetKey: templateDetails?.id || 'new',
        savedAttachments: templateDetails?.attachments || [],
    });

    useEffect(() => {
        if (setBase) {
            setBase({
                dirty: attachmentDirty,
                error: attachmentError || attachmentValidationError,
                files: attachmentDirty ? attachmentPayload : undefined,
                pending: attachmentPending,
            });
        }
    }, [attachmentDirty, attachmentError, attachmentPayload, attachmentPending, attachmentValidationError, setBase]);

    const checkValidationForAutoSave = () => !isEmpty(values.name) && !isEmpty(values.subject) && !isEmpty(values.category) && !isEmpty(superData.practices);

    useEffect(() => {
        const handleAutoSave = () => {
            if (isAutoSaveOn && !isEmpty(templateDetails) && checkValidationForAutoSave() && campaign && values.use_unlayer && autoSaveFunction && !attachmentPending && !attachmentBlocked) {
                setIsAutoSaveOn(false);
                onSubmit(values, false);
            }
        };
        handleAutoSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSaveFunction, attachmentPending, attachmentBlocked]);

    useEffect(() => {
        const latestName = values.use_unlayer ? 'unlayer_html' : name;
        if (values[latestName] === '<p><br></p>') {
            setValue(latestName, '', {
                shouldValidate: !isSubmitSuccessful,
            });
        }
    }, [isSubmitSuccessful, name, values.use_unlayer, setValue, values]);

    useEffect(() => {
        if (mergeTags) {
            const modifiedData = {};
            // eslint-disable-next-line no-restricted-syntax
            for (const mergeTag of mergeTags) {
                modifiedData[mergeTag.tag] = {
                    name: mergeTag.tag,
                    sample: mergeTag.tag,
                    value: formatMergeTagToken(mergeTag.tag),
                };
            }
            setCustomMergeTags(modifiedData);
        }
    }, [mergeTags]);

    const handleImageLibrary = (data, done) => {
        const handleClick = (e) => {
            if (e.target.classList.contains('library-image') && e.target.closest('#mediaLibrary')) {
                if (e.target.getAttribute('src')) {
                    onCloseCustomLib();
                    setInputFiles(null);
                    done({ url: e.target.getAttribute('src') });
                    dispatch(getMediaImages({ practiceId, limit: 50, offset: 0, shouldReset: true }));
                }
                // Remove the event listener after handling the click
                document.removeEventListener('click', handleClick);
            }
        };

        onOpenCustomLib();

        // Attach the event listener
        document.addEventListener('click', handleClick);
    };

    const handleIsUnlayerChange = (event) => {
        if (event.target.checked) {
            if (type === 'email_editor') {
                setValue('body', body || '');
            }
            isLoaded.current = true;
        }
        setValue('use_unlayer', event.target.checked);
    };

    const handleChangeEvent = (value, type) => {
        setIsAutoSaveOn(true);
        if (values.use_unlayer) {
            EditorMessage?.current?.editor?.setMergeTags(customMergeTags);
            EditorMessage?.current?.editor?.registerCallback('image', (file, done) => {
                const reader = new FileReader();
                reader.readAsDataURL(file?.accepted[0]);
                reader.onload = () => {
                    const detail = {
                        image: reader.result
                    };
                    dispatch(uploadImage(detail, (data) => {
                        if (data.success) {
                            const { image } = data.data;
                            if (image) {
                                done({ progress: 100, url: image });
                            }
                            return;
                        }
                        dispatch(updateError({ success: false, message: data.message.image.toString() }));
                    }));
                };
            });
            EditorMessage?.current?.editor?.exportHtml((data) => {
                const { html, design } = data;
                setValue('unlayer_html', html);
                setValue('unlayer_template', design);
            }, {
                minify: true,
            });
            return;
        }
        if (type === 'body' && value !== '<p><br></p>') {
            setValue('body', value);
            setBody(value);
        } else if (value === '<p><br></p>') {
            setValue('body', '');
            setBody('');
        }
    };

    const handleAttachment = (event, value, perform) => {
        if (perform === 'clear') {
            removeSavedAttachment(value, event);
            return;
        }
        replaceNewFiles(event);
    };

    return <Editor
        id={name}
        value={body}
        EditorMessage={EditorMessage}
        onChange={(value) => { handleChangeEvent(value, 'body'); }}
        error={values.use_unlayer ? !!errors.unlayer_html : !!errors.body}
        helperText={
            (values.use_unlayer ? !!errors.unlayer_html : !!errors.body) && (
                <FormHelperText error={values.use_unlayer ? !!errors.unlayer_html : !!errors.body} sx={{ px: 2 }}>
                    {values.use_unlayer ? (errors.unlayer_html?.message || 'This field id required') : (errors.body?.message || 'This field id required')}
                </FormHelperText>
            )
        }
        campaign={campaign}
        screenType={screenType}
        useUnlayer={values.use_unlayer}
        handleAttachment={handleAttachment}
        file={{ attachments: savedAttachments }}
        attachmentFiles={attachmentFiles}
        attachmentError={attachmentError?.message || ''}
        attachmentPending={attachmentPending}
        fileSize={attachmentValidationError || {}}
        onLoad={() => {
            if (isLoaded?.current) {
                isLoaded.current = false;
                EditorMessage?.current?.editor?.registerCallback('selectImage', handleImageLibrary);
                if (!isEmpty(templateDetails?.unlayer_template)) {
                    EditorMessage?.current?.editor?.loadDesign(templateDetails?.unlayer_template);
                    EditorMessage?.current?.editor?.setMergeTags(customMergeTags);
                }
                if (!isEmpty(values.unlayer_template)) {
                    EditorMessage?.current?.editor?.loadDesign(values.unlayer_template);
                    EditorMessage?.current?.editor?.setMergeTags(customMergeTags);
                }
            }
        }}
        handleIsUnlayerChange={handleIsUnlayerChange}
        {...other}
    />;
}

RHFEditor.propTypes = {
    name: PropTypes.string,
    screenType: PropTypes.string,
    EditorMessage: PropTypes.any,
    onOpenCustomLib: PropTypes.func,
    onCloseCustomLib: PropTypes.func,
    setInputFiles: PropTypes.func,
    setBase: PropTypes.func,
    campaign: PropTypes.bool,
    templateDetails: PropTypes.any,
    body: PropTypes.string,
    setBody: PropTypes.func,
    onSubmit: PropTypes.func,
};
